import { useMemo, useState } from 'react';
import { Alert, Button, Card, Input } from '@dreamtree-org/twreact-ui';
import SettingsEditor from '@/components/settings/SettingsEditor';
import { adminRequest, errorOf } from '@/lib/tenantAuth';
import { getAuth } from '@rbac/permissions';

/**
 * Company settings (screens C09–C12): branding, locale, SMTP, phone sign-in (Firebase) and
 * sign-in policy — the same registry-driven editor the platform console uses, against this
 * company only (routes/adminRouter.js /settings). Changes need a two-step-verified session;
 * the server refuses otherwise.
 */
export default function SettingsAdmin() {
  const auth = getAuth() || {};
  const branches = (auth.branches || []).map((b) => ({ id: b.id, code: b.code || b.name }));
  const [testTo, setTestTo] = useState('');
  const [testMsg, setTestMsg] = useState(null);

  const source = useMemo(() => ({
    // The admin view carries its own definitions (already filtered to what this caller may see).
    definitions: async () => (await adminRequest('get', '/settings')).definitions,
    load: (branchId) => adminRequest('get', branchId ? `/settings?branchId=${encodeURIComponent(branchId)}` : '/settings'),
    save: (branchId, writes, expectedVersion) => adminRequest('put', '/settings', { branchId, writes, expectedVersion }),
    errorMessage: (ex) => errorOf(ex).message,
  }), []);

  const sendTest = async () => {
    setTestMsg(null);
    try {
      await adminRequest('post', '/settings/smtp/test', { to: testTo });
      setTestMsg({ ok: true, text: `Test message sent to ${testTo}` });
    } catch (ex) {
      setTestMsg({ ok: false, text: errorOf(ex).message });
    }
  };

  return (
    <div className="p-4 space-y-4">
      <div>
        <h1 className="text-xl font-semibold">Company settings</h1>
        <p className="text-sm text-secondary-500">Branding, email delivery, phone sign-in and sign-in policy for your company.</p>
      </div>
      <SettingsEditor source={source} branches={branches}>
        <Card className="p-3 space-y-2">
          <h3 className="font-semibold">Send a test email</h3>
          <p className="text-xs text-secondary-500">Uses the saved SMTP profile — save your changes first.</p>
          <div className="flex flex-wrap items-end gap-2">
            <div className="w-full sm:w-72"><Input label="Send to" type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} /></div>
            <Button size="sm" variant="secondary" onClick={sendTest} disabled={!testTo.includes('@')}>Send test</Button>
          </div>
          {testMsg && <Alert variant={testMsg.ok ? 'success' : 'error'}>{testMsg.text}</Alert>}
        </Card>
      </SettingsEditor>
    </div>
  );
}
